import React, { useState, useEffect } from 'react';

import Modal from 'react-bootstrap/Modal';

import closeIcon from '../../assets/images/closeIcon.svg';
import FilledButton from '../../components/Buttons/FilledButton';
import FolderTreeSelector from '../../components/FolderTreeSelector';
import FolderService from '../../services/FolderService';
import classes from './styles.module.scss';

export default function MoveModal({
  isOpen,
  onClose,
  itemType,
  itemId,
  itemName,
  currentFolderId,
  allowedFolderIds,
  onMove,
  isOrgOwner = false,
  /** When false, root is hidden and cannot be selected (e.g. My Videos Move). Defaults to isOrgOwner. */
  allowRoot = isOrgOwner,
}) {
  const [selectedFolderId, setSelectedFolderId] = useState(null);
  const [selectedFolderName, setSelectedFolderName] = useState(null);
  const [currentFolderName, setCurrentFolderName] = useState(null);
  const [isMoving, setIsMoving] = useState(false);

  // Fetch current folder name when modal opens
  useEffect(() => {
    if (isOpen && currentFolderId) {
      FolderService.getFolder({
        id: currentFolderId,
        page: 1,
        limit: 1,
      })
        .then((folder) => {
          setCurrentFolderName(folder?.data?.name || 'Unknown');
        })
        .catch((error) => {
          console.error('Error fetching current folder:', error);
          setCurrentFolderName('Unknown');
        });
    } else if (isOpen && !currentFolderId) {
      setCurrentFolderName(null);
    }
  }, [isOpen, currentFolderId]);

  // Reset selection when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedFolderId(null);
      setSelectedFolderName(null);
    }
  }, [isOpen]);

  const handleFolderSelect = (folderId) => {
    // folderId will be null for root, '' for root node in tree, or a folder ID
    // Convert null to '' for tree display, keep '' as is
    const treeFolderId = folderId === null ? '' : folderId;
    
    // Validate that the selected folder is in the allowed list (for folder owners)
    if (allowedFolderIds && treeFolderId && !allowedFolderIds.includes(treeFolderId)) {
      return;
    }
    
    setSelectedFolderId(treeFolderId);
    // Fetch folder name
    if (treeFolderId) {
      FolderService.getFolder({
        id: treeFolderId,
        page: 1,
        limit: 1,
      })
        .then((folder) => {
          setSelectedFolderName(folder?.data?.name || 'Unknown');
        })
        .catch((error) => {
          console.error('Error fetching selected folder:', error);
          setSelectedFolderName('Unknown');
        });
    } else {
      setSelectedFolderName(null);
    }
  };


  const handleMove = async () => {
    // When allowRoot: null or '' means root. Otherwise a folder must be selected.
    if (
      selectedFolderId === undefined ||
      (selectedFolderId === null && !allowRoot) ||
      (selectedFolderId === '' && !allowRoot)
    ) {
      return;
    }

    setIsMoving(true);
    try {
      // Convert empty string (or null with allowRoot) to null for root
      const destinationId =
        selectedFolderId === '' || selectedFolderId === null
          ? null
          : selectedFolderId;
      if (!allowRoot && !destinationId) {
        setIsMoving(false);
        return;
      }
      await onMove(destinationId);
      // onMove handles success notification and closing
      setIsMoving(false);
    } catch (error) {
      // Error notification is already handled by mutation's onError
      console.error('[MoveModal] Error in handleMove:', error);
      setIsMoving(false);
    }
  };

  const handleClose = () => {
    if (!isMoving) {
      setSelectedFolderId(null);
      setSelectedFolderName(null);
      setCurrentFolderName(null);
      onClose();
    }
  };

  const getCurrentLocationText = () => {
    if (!currentFolderId) {
      return 'Root';
    }
    return currentFolderName || 'Loading...';
  };

  const getTargetLocationText = () => {
    if (selectedFolderId === null || selectedFolderId === undefined) {
      return 'Not selected';
    }
    if (selectedFolderId === '') {
      return '/';
    }
    return selectedFolderName || 'Loading...';
  };

  return (
    <Modal
      show={isOpen}
      centered
      contentClassName={classes.MoveModal}
      dialogClassName={classes.dialog}
      backdropClassName={classes.backdrop}
      onHide={handleClose}
    >
      <i className={classes.closeIcon} onClick={handleClose}>
        <img src={closeIcon} alt="Close" />
      </i>
      <div className={classes.content}>
        <h2>Move {itemName}</h2>
        <div className={classes.warning}>
          IMPORTANT: Moving a parent folder under another will make it inherit
          that folder’s permissions.
        </div>
        <div className={classes.locationInfo}>
          <div className={classes.locationItem}>
            <span className={classes.locationLabel}>Current location:</span>
            <span className={classes.locationValue}>
              {getCurrentLocationText()}
            </span>
            <span className={classes.locationLabel}>Target location:</span>
            <span className={classes.locationValue}>
              {getTargetLocationText()}
            </span>
          </div>
        </div>
        <div className={classes.treeContainer}>
          <FolderTreeSelector
            showCheckboxes={false}
            showMeetings={false}
            showRoot={allowRoot}
            excludeFolderId={itemType === 'folder' ? itemId : undefined}
            allowedFolderIds={allowedFolderIds || undefined}
            currentFolderId={currentFolderId || undefined}
            selectedFolderId={selectedFolderId}
            onFolderSelect={handleFolderSelect}
            isSubmitting={isMoving}
          />
        </div>
        <div className={classes.actions}>
          <FilledButton
            isDisabled={
              (selectedFolderId === null && !allowRoot) ||
              selectedFolderId === undefined ||
              (selectedFolderId === '' && !allowRoot) ||
              isMoving
            }
            onClick={handleMove}
          >
            {isMoving ? 'Moving...' : 'Move'}
          </FilledButton>
        </div>
      </div>
    </Modal>
  );
}

